// src/appData.ts
function createAppData(opts) {
  const appDataObj = {
    getCurrentEnterprise() {
      const empresa = opts.session.get()?.empresa;
      return empresa ?? null;
    },
    getCurrentUser() {
      const s = opts.session.get();
      if (!s) return null;
      return {
        ID: s.userId,
        EMPRESA: s.empresa,
        ROL: s.rol
      };
    },
    async login(payload) {
      try {
        const p = payload;
        const deviceId = opts.macros.get("DEVICE_ID") ?? "aaaabbbbccccddddaaaabbbbccccdddd";
        const userName = p.userName ?? p.user ?? p.USUARIO ?? "";
        const password = p.password ?? p.PASSWORD ?? "";
        const noAuth = p.noAuth === true || password === "";
        const body = {
          USUARIO: userName,
          PASSWORD: password,
          DEVICE_ID: p.deviceId ?? p.DEVICE_ID ?? deviceId,
          PLATFORM: p.platform ?? p.PLATFORM ?? "Web",
          APP_VERSION: opts.macros.get("APP_VERSION") ?? "1.0.0",
          ...p.enterprise || p.ID_EMPRESA ? { ID_EMPRESA: p.enterprise ?? p.ID_EMPRESA } : {},
          ...noAuth ? { NO_AUTH: true } : {}
        };
        const entryPointOverride = p.entryPoint;
        const res = await opts.http.post("/api/v1/auth/login", body);
        if (!res?.ACCESS_TOKEN) return null;
        const session = {
          token: res.ACCESS_TOKEN,
          refreshToken: res.REFRESH_TOKEN,
          userId: res.ID_USUARIO,
          empresa: res.ID_EMPRESA,
          rol: res.ROL,
          expiresAt: res.EXPIRES_AT
        };
        opts.session.set(session);
        opts.macros.set("TOKEN", res.ACCESS_TOKEN ?? "");
        opts.macros.set("REFRESH_TOKEN", res.REFRESH_TOKEN ?? "");
        opts.macros.set("USERID", String(res.ID_USUARIO ?? ""));
        opts.macros.set("EMPRESA", String(res.ID_EMPRESA ?? ""));
        opts.macros.set("ROL", res.ROL ?? "");
        if (res.MACROS_SESION) {
          for (const [k, v] of Object.entries(res.MACROS_SESION)) {
            opts.macros.set(k, v);
          }
        }
        const entryPoint = entryPointOverride ?? opts.macros.get("ENTRY_POINT");
        if (entryPoint && typeof window !== "undefined") {
          const ui = window.ui;
          if (ui?.openCollection) ui.openCollection(entryPoint);
          else if (ui?.openMenu) ui.openMenu(entryPoint);
        }
        return session;
      } catch (err) {
        console.error("[appData.login]", err);
        return null;
      }
    },
    async logout() {
      opts.session.clear();
    },
    getCollection(name) {
      if (opts.dataLayerGetCollection) {
        return opts.dataLayerGetCollection(name);
      }
      return {
        getName() {
          return name;
        },
        async loadAll(_filter, _sort) {
        },
        getCurrentItem() {
          return null;
        },
        async createObject(_initial) {
          return null;
        }
      };
    },
    setGlobalMacro(name, value) {
      opts.macros.set(name, value == null ? null : String(value));
    },
    getGlobalMacro(name) {
      return opts.macros.get(name);
    },
    failWithMessage(code, msg) {
      console.error(`[appData.failWithMessage] code=${code} msg=${msg}`);
    },
    exit() {
      opts.session.clear();
      if (typeof window !== "undefined") {
        const loginColl = opts.macros.get("LOGIN_COLLECTION");
        const ui = window.ui;
        if (loginColl && ui?.openCollection) {
          ui.openCollection(loginColl);
        } else {
          window.location.href = "/";
        }
      }
    },
    pushValue(value) {
      if (typeof sessionStorage === "undefined") return;
      try {
        const raw = sessionStorage.getItem("xone:valueStack");
        const stack = raw ? JSON.parse(raw) : [];
        stack.push(value);
        sessionStorage.setItem("xone:valueStack", JSON.stringify(stack));
      } catch {
      }
    },
    popValue() {
      if (typeof sessionStorage === "undefined") return void 0;
      try {
        const raw = sessionStorage.getItem("xone:valueStack");
        if (!raw) return void 0;
        const stack = JSON.parse(raw);
        const value = stack.pop();
        sessionStorage.setItem("xone:valueStack", JSON.stringify(stack));
        return value;
      } catch {
        return void 0;
      }
    },
    pushValueAndExit(value) {
      appDataObj.pushValue?.(value);
      if (typeof window !== "undefined") history.back();
    },
    isPhone() {
      if (typeof window === "undefined") return false;
      return window.matchMedia("(max-width: 767px)").matches;
    },
    isTablet() {
      if (typeof window === "undefined") return false;
      return window.matchMedia("(min-width: 768px) and (max-width: 1024px)").matches;
    },
    isVertical() {
      if (typeof window === "undefined") return true;
      return window.matchMedia("(orientation: portrait)").matches;
    },
    isHorizontal() {
      if (typeof window === "undefined") return false;
      return window.matchMedia("(orientation: landscape)").matches;
    },
    getAppPath() {
      return "/";
    },
    getFilesPath() {
      return "/files/";
    },
    writeConsoleString(msg) {
      console.log(msg);
    },
    executeSql(_sql) {
      console.warn("[appData.executeSql] No soportado en el canal web \u2014 los datos vienen del backend.");
      return null;
    }
  };
  return appDataObj;
}

// src/bundle.ts
function createBundleCtor() {
  class XoneBundle {
    data;
    constructor(initial) {
      this.data = initial ? { ...initial } : {};
    }
    get(key) {
      return this.data[key];
    }
    set(key, value) {
      this.data[key] = value;
    }
    has(key) {
      return key in this.data;
    }
    keys() {
      return Object.keys(this.data);
    }
  }
  return XoneBundle;
}

// src/crypto.ts
function createCryptoApi() {
  return {
    async sha256(input) {
      return digestHex("SHA-256", input);
    },
    async sha1(input) {
      return digestHex("SHA-1", input);
    },
    async sha384(input) {
      return digestHex("SHA-384", input);
    },
    async sha512(input) {
      return digestHex("SHA-512", input);
    },
    async md5(input) {
      return md5Hash(input);
    },
    async hmacSha256(key, input) {
      const keyData = new TextEncoder().encode(key);
      const cryptoKey = await window.crypto.subtle.importKey(
        "raw",
        keyData,
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"]
      );
      const sig = await window.crypto.subtle.sign(
        "HMAC",
        cryptoKey,
        new TextEncoder().encode(input)
      );
      return toHex(new Uint8Array(sig));
    },
    randomBytes(length) {
      const arr = new Uint8Array(length);
      window.crypto.getRandomValues(arr);
      return arr;
    },
    base64Encode(input) {
      return btoa(unescape(encodeURIComponent(input)));
    },
    base64Decode(input) {
      return decodeURIComponent(escape(atob(input)));
    },
    toBase64(input) {
      return btoa(unescape(encodeURIComponent(input)));
    },
    fromBase64(input) {
      return decodeURIComponent(escape(atob(input)));
    },
    getNewUuid() {
      if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID().replace(/-/g, "");
      }
      return Array.from(
        { length: 32 },
        () => Math.floor(Math.random() * 16).toString(16)
      ).join("");
    },
    getRandomString(length) {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      const arr = new Uint8Array(length);
      window.crypto.getRandomValues(arr);
      return Array.from(arr, (b) => chars[b % chars.length]).join("");
    },
    getRandomInt(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },
    getRandomDouble() {
      return Math.random();
    },
    getChecksum(input) {
      let hash = 0;
      for (let i = 0; i < input.length; i++) {
        hash = (hash << 5) - hash + input.charCodeAt(i) | 0;
      }
      return Math.abs(hash).toString(16).padStart(8, "0");
    }
  };
}
async function digestHex(algo, input) {
  const data = new TextEncoder().encode(String(input));
  const buf = await window.crypto.subtle.digest(algo, data);
  return toHex(new Uint8Array(buf));
}
function toHex(bytes) {
  let out = "";
  for (const b of bytes) out += b.toString(16).padStart(2, "0");
  return out;
}
function md5Hash(input) {
  const bytes = new TextEncoder().encode(input);
  const len = bytes.length;
  const words = new Uint32Array(len + 72 >>> 6 << 4);
  const view = new DataView(words.buffer);
  for (let i = 0; i < len; i++) view.setUint8(i, bytes[i]);
  view.setUint8(len, 128);
  view.setUint32(words.length - 2 << 2, len << 3, true);
  const S = [7, 12, 17, 22, 5, 9, 14, 20, 4, 11, 16, 23, 6, 10, 15, 21];
  const K = new Uint32Array(64);
  for (let i = 0; i < 64; i++) K[i] = Math.floor(2 ** 32 * Math.abs(Math.sin(i + 1)));
  let a0 = 1732584193, b0 = 4023233417, c0 = 2562383102, d0 = 271733878;
  for (let off = 0; off < words.length; off += 16) {
    let a = a0, b = b0, c = c0, d = d0;
    for (let i = 0; i < 64; i++) {
      let f, g;
      if (i < 16) {
        f = b & c | ~b & d;
        g = i;
      } else if (i < 32) {
        f = d & b | ~d & c;
        g = (5 * i + 1) % 16;
      } else if (i < 48) {
        f = b ^ c ^ d;
        g = (3 * i + 5) % 16;
      } else {
        f = c ^ (b | ~d);
        g = 7 * i % 16;
      }
      const m = view.getUint32(off + g << 2, true);
      const temp = d;
      d = c;
      c = b;
      const sum = a + f + K[i] + m >>> 0;
      const s = S[(i >>> 4 << 2) + (i & 3)];
      b = b + ((sum << s | sum >>> 32 - s) >>> 0) >>> 0;
      a = temp;
    }
    a0 = a0 + a >>> 0;
    b0 = b0 + b >>> 0;
    c0 = c0 + c >>> 0;
    d0 = d0 + d >>> 0;
  }
  const result = new DataView(new ArrayBuffer(16));
  result.setUint32(0, a0, true);
  result.setUint32(4, b0, true);
  result.setUint32(8, c0, true);
  result.setUint32(12, d0, true);
  return toHex(new Uint8Array(result.buffer));
}

// src/dataLayer.ts
import { createBackendClient } from "@xone-web/backend-client";
import {
  createDataCollection,
  createRemoteStorage,
  XoneStructuredDataClient
} from "@xone-web/data";
import { create$Http } from "@xone-web/http";
function createDataLayer(opts) {
  const cache = /* @__PURE__ */ new Map();
  let bundlePromise = null;
  function ensureBundle() {
    if (bundlePromise) return bundlePromise;
    bundlePromise = (async () => {
      const $http = create$Http({
        macroReader: {
          getGlobalMacro: (n) => opts.macros.get(n)
        }
      });
      const backendClient = createBackendClient({
        apiBase: opts.apiBase,
        $http,
        macros: {
          getGlobalMacro: (n) => opts.macros.get(n)
        }
      });
      const remote = createRemoteStorage({
        backend: backendClient
      });
      const structuredClient = new XoneStructuredDataClient(
        backendClient
      );
      return { adapter: remote.adapter, structuredClient };
    })();
    return bundlePromise;
  }
  function getCollection(name) {
    const existing = cache.get(name);
    if (existing) return existing;
    const wrapper = createWrapper(name, ensureBundle, opts.manifestPromise);
    cache.set(name, wrapper);
    return wrapper;
  }
  return {
    getCollection,
    getStorage: () => ensureBundle().then((b) => b.adapter)
  };
}
function createWrapper(name, ensureBundle, manifestPromise) {
  let inner = null;
  let initPromise = null;
  let ghostWarnLogged = false;
  function ensure() {
    if (inner) return Promise.resolve(inner);
    if (initPromise) return initPromise;
    initPromise = (async () => {
      const manifest = await manifestPromise;
      const def = manifest?.collections?.[name];
      if (!def) {
        if (!ghostWarnLogged) {
          ghostWarnLogged = true;
          console.warn(
            `[xone] appData.getCollection("${name}"): no definida en manifest. Devolvendo ghost (sin datos remotos).`
          );
        }
        return null;
      }
      const bundle = await ensureBundle();
      const xneDef = def;
      inner = createDataCollection({
        name,
        definition: xneDef,
        storage: bundle.adapter,
        structuredClient: bundle.structuredClient
      });
      return inner;
    })();
    return initPromise;
  }
  return {
    getName() {
      return name;
    },
    async loadAll(filter, sort) {
      const c = await ensure();
      if (!c) return;
      if (filter !== void 0) c.setFilter(filter);
      if (sort !== void 0) c.setSort(sort);
      await c.loadAll();
    },
    async reload() {
      const c = await ensure();
      if (!c) return;
      await c.reload();
    },
    getCurrentItem() {
      if (!inner) return null;
      return inner.getCurrentItem();
    },
    async findAllObjects(...args) {
      const c = await ensure();
      if (!c) return [];
      return c.findAllObjects(...args);
    },
    async createObject(initial) {
      const c = await ensure();
      if (!c) {
        throw new Error(
          `[xone] No se puede createObject("${name}"): colecci\xF3n no definida en el manifest.`
        );
      }
      return c.createObject(initial);
    },
    async ready() {
      const c = await ensure();
      if (!c) {
        throw new Error(
          `[xone] Colecci\xF3n "${name}" no definida en el manifest.`
        );
      }
      return c;
    },
    async startBrowse(_returnCount) {
      const c = await ensure();
      if (!c) return 0;
      await c.startBrowse();
      return c.browseLength();
    },
    isEOF() {
      if (!inner) return true;
      return inner.isEOF();
    },
    async moveNext() {
      if (!inner) return false;
      return inner.moveNext();
    },
    endBrowse() {
      if (!inner) return;
      void inner.endBrowse();
    },
    setFilter(filter) {
      if (!inner) return;
      inner.setFilter(filter);
    },
    getFilter() {
      if (!inner) return "";
      return inner.getFilter();
    },
    setSort(sort) {
      if (!inner) return;
      inner.setSort(sort);
    },
    getSort() {
      if (!inner) return "";
      return inner.getSort();
    },
    getCount() {
      if (!inner) return 0;
      return inner.getCount();
    },
    getObject(indexOrKey) {
      if (!inner) return null;
      return inner.get(indexOrKey);
    },
    get(indexOrKey) {
      if (!inner) return null;
      return inner.get(indexOrKey);
    },
    async deleteItem(obj) {
      const c = await ensure();
      if (!c) return false;
      return c.deleteItem(obj);
    },
    async saveAll() {
      const c = await ensure();
      if (!c) return false;
      return c.saveAll();
    },
    clear() {
      if (!inner) return;
      inner.clear();
    }
  };
}

// src/domSync.ts
function createDomSync(opts) {
  function syncField(field) {
    if (typeof document === "undefined") return;
    const value = opts.getSelf().toJson()[field];
    if (value === void 0) return;
    const str = value === null ? "" : String(value);
    const displayEls = document.querySelectorAll(
      `[data-prop="${field}"] .xone-text-value, span[data-prop="${field}"], div[data-prop="${field}"]`
    );
    for (const el of displayEls) {
      if (el.hasAttribute("data-xone-static") && str === "" && el.textContent !== "") continue;
      if (el.textContent !== str) el.textContent = str;
    }
    const inputs = document.querySelectorAll(
      `[data-prop="${field}"] input, [data-prop="${field}"] textarea`
    );
    for (const inp of inputs) {
      if (inp.value !== str) inp.value = str;
    }
  }
  function syncAll() {
    if (typeof document === "undefined") return;
    const fields = opts.getSelf().toJson();
    for (const field of Object.keys(fields)) {
      syncField(field);
    }
  }
  return {
    syncAll,
    syncField,
    dispose() {
    }
  };
}

// src/http.ts
function createHttp(opts) {
  function resolveUrl(url) {
    const expanded = opts.macros.expand(url);
    if (/^https?:\/\//i.test(expanded)) return expanded;
    if (expanded.startsWith("/")) return expanded;
    const base = opts.apiBase ?? "";
    return `${base.replace(/\/$/, "")}/${expanded.replace(/^\//, "")}`;
  }
  function buildHeaders(init) {
    const h = new Headers(init?.headers ?? {});
    if (!h.has("Accept")) h.set("Accept", "application/json");
    const s = opts.session.get();
    if (s?.token) h.set("Authorization", `Bearer ${s.token}`);
    return h;
  }
  async function request(method, url, body, init) {
    const finalUrl = resolveUrl(url);
    const headers = buildHeaders(init);
    let payload;
    if (body !== void 0 && body !== null) {
      if (body instanceof Blob || body instanceof FormData || body instanceof URLSearchParams || typeof body === "string") {
        payload = body;
      } else {
        payload = JSON.stringify(body);
        if (!headers.has("Content-Type"))
          headers.set("Content-Type", "application/json");
      }
    }
    const res = await fetch(finalUrl, {
      method,
      ...init,
      headers,
      body: payload
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`[$http] ${method} ${finalUrl} \u2192 ${res.status}: ${text.slice(0, 200)}`);
    }
    const ct = res.headers.get("content-type") ?? "";
    if (ct.includes("application/json")) {
      return await res.json();
    }
    return await res.text();
  }
  return {
    get(url, init) {
      return request("GET", url, void 0, init);
    },
    post(url, body, init) {
      return request("POST", url, body, init);
    },
    put(url, body, init) {
      return request("PUT", url, body, init);
    },
    delete(url, init) {
      return request("DELETE", url, void 0, init);
    }
  };
}

// src/macros.ts
var MACRO_RE = /##([A-Za-z0-9_]+)##/g;
function createMacroStore() {
  const store = /* @__PURE__ */ new Map();
  const get = (name) => {
    const v = store.get(name);
    return v ?? null;
  };
  const set = (name, value) => {
    if (value == null) {
      store.delete(name);
      return;
    }
    store.set(name, String(value));
  };
  const expand = (text) => {
    if (typeof text !== "string") return text;
    if (!text.includes("##")) return text;
    return text.replace(MACRO_RE, (full, name) => {
      const v = store.get(name);
      return v ?? full;
    });
  };
  const snapshot = () => {
    return Object.fromEntries(store);
  };
  return { get, set, expand, snapshot };
}

// src/session.ts
var KEY = "xone:auth";
function createSession(opts) {
  let cached = null;
  if (opts.storage) {
    try {
      const raw = opts.storage.getItem(KEY);
      if (raw) {
        cached = JSON.parse(raw);
      }
    } catch {
      cached = null;
    }
  }
  function persist(s) {
    if (!opts.storage) return;
    try {
      if (s === null) opts.storage.removeItem(KEY);
      else opts.storage.setItem(KEY, JSON.stringify(s));
    } catch {
    }
  }
  function syncMacros(s) {
    opts.macros.set("USERID", s?.userId !== void 0 ? String(s.userId) : null);
    opts.macros.set("EMPRESA", s?.empresa !== void 0 ? String(s.empresa) : null);
    opts.macros.set("TOKEN", s?.token ?? null);
    opts.macros.set("ROL", s?.rol ?? null);
  }
  syncMacros(cached);
  return {
    get() {
      return cached;
    },
    set(s) {
      cached = s;
      persist(s);
      syncMacros(s);
    },
    clear() {
      cached = null;
      persist(null);
      syncMacros(null);
    },
    isAuthenticated() {
      if (!cached) return false;
      if (cached.expiresAt && cached.expiresAt < Date.now()) {
        cached = null;
        persist(null);
        syncMacros(null);
        return false;
      }
      return Boolean(cached.token);
    }
  };
}

// src/ui.ts
function createUi(opts) {
  function urlFor(name) {
    const direct = opts.routeMap.get(name);
    if (direct) return direct;
    const slug = slugify(name);
    return `/${slug}/`;
  }
  return {
    openCollection(name, params) {
      const url = withQuery(urlFor(name), params);
      navigateWithViewTransition(url);
    },
    back() {
      history.back();
    },
    showToast(msg) {
      if (typeof msg === "object" && msg !== null) {
        showToast(opts.macros.expand(msg.text ?? ""), {
          bgColor: msg.color,
          textColor: msg.textColor,
          rounded: msg.rounded
        });
      } else {
        showToast(opts.macros.expand(msg));
      }
    },
    async showDialog(o) {
      const message = typeof o === "string" ? o : o.message ?? "";
      const title = typeof o === "string" ? void 0 : o.title;
      await openNativeDialog({ title, message: opts.macros.expand(message) });
    },
    async confirm(msg) {
      return openNativeConfirm(opts.macros.expand(msg));
    },
    async prompt(msg, defaultValue) {
      return Promise.resolve(window.prompt(opts.macros.expand(msg), defaultValue ?? ""));
    },
    showWaitDialog(msg) {
      if (typeof document === "undefined") return;
      let overlay = document.querySelector("[data-xone-wait]");
      if (!overlay) {
        overlay = document.createElement("div");
        overlay.setAttribute("data-xone-wait", "");
        overlay.style.cssText = [
          "position:fixed",
          "inset:0",
          "z-index:10000",
          "display:flex",
          "align-items:center",
          "justify-content:center",
          "background:rgba(0,0,0,0.3)"
        ].join(";");
        const inner2 = document.createElement("div");
        inner2.style.cssText = "background:#fff;padding:16px 24px;border-radius:8px;font-size:14px;box-shadow:0 4px 16px rgba(0,0,0,0.2);";
        overlay.appendChild(inner2);
        document.body.appendChild(overlay);
      }
      const inner = overlay.firstElementChild;
      if (inner) inner.textContent = opts.macros.expand(msg ?? "Cargando...");
      overlay.style.display = "flex";
    },
    hideWaitDialog() {
      if (typeof document === "undefined") return;
      const overlay = document.querySelector("[data-xone-wait]");
      if (overlay) overlay.style.display = "none";
    },
    refreshView() {
      opts.domSync?.syncAll();
    },
    refresh() {
      opts.domSync?.syncAll();
    },
    refreshValue(field) {
      opts.domSync?.syncField(field);
    },
    setGroupVisible(id, visible) {
      if (typeof document === "undefined") return;
      const el = document.querySelector(`[data-group-id="${id}"]`);
      if (el) el.style.display = visible ? "" : "none";
    },
    showGroup(id) {
      this.setGroupVisible(id, true);
    },
    hideGroup(id) {
      this.setGroupVisible(id, false);
    },
    getView(_self) {
      return {
        exit() {
          history.back();
        },
        refresh(...fields) {
          if (fields.length === 0) {
            opts.domSync?.syncAll();
          } else {
            for (const f of fields) opts.domSync?.syncField(f);
          }
        }
      };
    },
    async msgBox(message, title, buttons) {
      const expanded = opts.macros.expand(message);
      if (buttons === 4) {
        const yes = await openNativeConfirm(
          title ? `${title}

${expanded}` : expanded
        );
        return yes ? 6 : 7;
      }
      await openNativeDialog({
        title,
        message: expanded
      });
      return 1;
    },
    openMenu(name, _self) {
      const url = withQuery(urlFor(name));
      navigateWithViewTransition(url);
    },
    openEditView(target, _exitCurrent) {
      if (typeof target === "string") {
        navigateWithViewTransition(urlFor(target));
      }
    },
    openURL(url) {
      if (typeof window !== "undefined") window.open(url, "_blank");
    },
    isNetworkAvailable() {
      return typeof navigator !== "undefined" ? navigator.onLine : true;
    },
    getOrientation() {
      if (typeof screen !== "undefined" && screen.orientation) {
        return screen.orientation.type.includes("portrait") ? "portrait" : "landscape";
      }
      return "portrait";
    },
    getScreenWidth() {
      return typeof screen !== "undefined" ? screen.width : 0;
    },
    getScreenHeight() {
      return typeof screen !== "undefined" ? screen.height : 0;
    }
  };
}
function navigateWithViewTransition(url) {
  if (typeof document === "undefined") return;
  const doc = document;
  if (typeof doc.startViewTransition === "function") {
    try {
      doc.startViewTransition(() => {
        window.location.href = url;
      });
      return;
    } catch {
    }
  }
  window.location.href = url;
}
function withQuery(url, params) {
  if (!params) return url;
  const entries = Object.entries(params).filter(([_, v]) => v != null);
  if (entries.length === 0) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of entries) qs.append(k, String(v));
  return url + (url.includes("?") ? "&" : "?") + qs.toString();
}
function slugify(name) {
  return name.replace(/_/g, "-").replace(/([a-z0-9])([A-Z])/g, "$1-$2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1-$2").toLowerCase().replace(/-+/g, "-").replace(/^-|-$/g, "");
}
function showToast(message, toastOpts) {
  if (typeof document === "undefined") return;
  let el = document.querySelector("[data-xone-toast]");
  if (!el) {
    el = document.createElement("div");
    el.setAttribute("data-xone-toast", "");
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    document.body.appendChild(el);
  }
  const bg = toastOpts?.bgColor ?? "rgba(0,0,0,0.85)";
  const fg = toastOpts?.textColor ?? "#fff";
  const radius = toastOpts?.rounded ? "20px" : "6px";
  el.style.cssText = [
    "position:fixed",
    "left:50%",
    "bottom:24px",
    "transform:translateX(-50%)",
    `background:${bg}`,
    `color:${fg}`,
    "padding:8px 16px",
    `border-radius:${radius}`,
    "font-size:14px",
    "z-index:9999",
    "max-width:80vw",
    "opacity:0",
    "transition:opacity 200ms"
  ].join(";");
  el.textContent = message;
  requestAnimationFrame(() => {
    el.style.opacity = "1";
  });
  window.clearTimeout(el.__toastTimer);
  el.__toastTimer = window.setTimeout(() => {
    el.style.opacity = "0";
  }, 3e3);
}
async function openNativeDialog(opts) {
  if (typeof document === "undefined" || typeof HTMLDialogElement === "undefined") {
    window.alert([opts.title, opts.message].filter(Boolean).join("\n\n"));
    return;
  }
  return new Promise((resolve) => {
    const dialog = document.createElement("dialog");
    dialog.setAttribute("data-xone-dialog", "");
    dialog.style.cssText = "border:none;border-radius:8px;padding:0;max-width:90vw;box-shadow:0 8px 32px rgba(0,0,0,0.2);";
    const inner = document.createElement("div");
    inner.style.cssText = "padding:20px;min-width:280px;";
    if (opts.title) {
      const h = document.createElement("h3");
      h.textContent = opts.title;
      h.style.cssText = "margin:0 0 12px;font-size:16px;";
      inner.appendChild(h);
    }
    const p = document.createElement("p");
    p.textContent = opts.message;
    p.style.cssText = "margin:0 0 16px;font-size:14px;white-space:pre-wrap;";
    inner.appendChild(p);
    const ok = document.createElement("button");
    ok.type = "button";
    ok.textContent = "OK";
    ok.style.cssText = "padding:6px 16px;border:none;background:#0066cc;color:#fff;border-radius:4px;cursor:pointer;float:right;";
    ok.addEventListener("click", () => {
      dialog.close();
      dialog.remove();
      resolve();
    });
    dialog.addEventListener("cancel", () => {
      dialog.remove();
      resolve();
    });
    inner.appendChild(ok);
    dialog.appendChild(inner);
    document.body.appendChild(dialog);
    dialog.showModal();
  });
}
async function openNativeConfirm(message) {
  if (typeof document === "undefined" || typeof HTMLDialogElement === "undefined") {
    return Promise.resolve(window.confirm(message));
  }
  return new Promise((resolve) => {
    const dialog = document.createElement("dialog");
    dialog.setAttribute("data-xone-confirm", "");
    dialog.style.cssText = "border:none;border-radius:8px;padding:0;max-width:90vw;box-shadow:0 8px 32px rgba(0,0,0,0.2);";
    const inner = document.createElement("div");
    inner.style.cssText = "padding:20px;min-width:280px;";
    const p = document.createElement("p");
    p.textContent = message;
    p.style.cssText = "margin:0 0 16px;font-size:14px;white-space:pre-wrap;";
    inner.appendChild(p);
    const actions = document.createElement("div");
    actions.style.cssText = "display:flex;justify-content:flex-end;gap:8px;";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.textContent = "Cancelar";
    cancel.style.cssText = "padding:6px 16px;border:1px solid #ccc;background:#fff;border-radius:4px;cursor:pointer;";
    cancel.addEventListener("click", () => {
      dialog.close();
      dialog.remove();
      resolve(false);
    });
    const ok = document.createElement("button");
    ok.type = "button";
    ok.textContent = "Aceptar";
    ok.style.cssText = "padding:6px 16px;border:none;background:#0066cc;color:#fff;border-radius:4px;cursor:pointer;";
    ok.addEventListener("click", () => {
      dialog.close();
      dialog.remove();
      resolve(true);
    });
    dialog.addEventListener("cancel", () => {
      dialog.remove();
      resolve(false);
    });
    actions.appendChild(cancel);
    actions.appendChild(ok);
    inner.appendChild(actions);
    dialog.appendChild(inner);
    document.body.appendChild(dialog);
    dialog.showModal();
  });
}
function buildRouteMap(manifest) {
  const map = /* @__PURE__ */ new Map();
  if (!manifest) return map;
  for (const r of manifest.routes) {
    map.set(r.collection, r.url);
  }
  return map;
}

// src/createAppContext.ts
import {
  createDataCollection as createDataCollection2,
  createDataObject,
  MemoryStorageAdapter
} from "@xone-web/data";
function createAppContext(opts = {}) {
  const macros = createMacroStore();
  const storage = opts.storage ?? (typeof window !== "undefined" && window.sessionStorage ? window.sessionStorage : null);
  const session = createSession({ storage, macros });
  if (typeof navigator !== "undefined") {
    macros.set("CURRENT_LANGUAGE", navigator.language ?? "es");
  }
  macros.set("API_BASE", opts.apiBase ?? "/api/v1");
  const routeMap = /* @__PURE__ */ new Map();
  if (opts.routeMap) {
    for (const [k, v] of Object.entries(opts.routeMap)) {
      routeMap.set(k, v);
    }
  }
  if (opts.tablePrefix) {
    macros.set("PREF", opts.tablePrefix);
  }
  if (opts.appName) {
    macros.set("APPNAME", opts.appName);
  }
  const http = createHttp({ macros, session, apiBase: opts.apiBase ?? "/api/v1" });
  const cryptoApi = createCryptoApi();
  const Bundle = createBundleCtor();
  const hasMpaRoutes = opts.routeMap !== void 0 && Object.keys(opts.routeMap).length > 0;
  const manifestUrl = opts.manifestUrl ?? "/xone-static-manifest.json";
  const manifestPromise = typeof fetch === "function" ? fetch(manifestUrl).then((r) => r.ok ? r.json() : null).then((m) => {
    if (!m) return null;
    if (!hasMpaRoutes) {
      const map = buildRouteMap(m);
      for (const [k, v] of map) routeMap.set(k, v);
      macros.set("PREF", m.project.tablePrefix);
      macros.set("APPNAME", m.project.title ?? m.project.name);
    }
    return m;
  }).catch((err) => {
    console.warn("[xone] manifest no cargado:", err);
    return null;
  }) : Promise.resolve(null);
  const dataLayer = createDataLayer({
    apiBase: opts.apiBase ?? "/api/v1",
    macros,
    session,
    manifestPromise
  });
  const appData = createAppData({
    http,
    macros,
    session,
    dataLayerGetCollection: dataLayer.getCollection
  });
  let self;
  let ThisDataColl;
  if (opts.collectionDefinition) {
    const def = opts.collectionDefinition;
    const isSpecial = def.special === true;
    if (isSpecial) {
      const memStorage = new MemoryStorageAdapter();
      const coll = createDataCollection2({
        name: opts.collectionName ?? def.name,
        definition: def,
        storage: memStorage
      });
      const obj = createDataObject({
        owner: coll,
        isNew: true
      });
      self = obj;
      ThisDataColl = coll;
    } else {
      const wrapper = dataLayer.getCollection(opts.collectionName ?? def.name);
      ThisDataColl = wrapper;
      const selfFields = /* @__PURE__ */ new Map();
      self = new Proxy(
        {
          getValue(field) {
            return selfFields.get(field);
          },
          setValue(field, value) {
            selfFields.set(field, value);
          },
          toJson() {
            return Object.fromEntries(selfFields);
          },
          save: async () => true,
          deleteObject: async () => true,
          refresh: async () => true,
          getDirty: () => false,
          isNew: () => true,
          getOldValue: () => void 0,
          getOwnerCollection: () => ThisDataColl,
          getPropertyNames: () => def.fields.map((f) => f.name),
          getAllFields: () => Object.fromEntries(selfFields),
          getFieldPropertyValue: () => void 0,
          setFieldPropertyValue: (_f, _a, v) => v,
          getVariable: () => void 0,
          setVariable: () => {
          },
          bind: () => {
          },
          unbind: () => {
          }
        },
        {
          get(target, prop) {
            if (typeof prop === "string" && !(prop in target)) {
              return selfFields.get(prop);
            }
            return target[prop];
          },
          set(_target, prop, value) {
            if (typeof prop === "string") {
              selfFields.set(prop, value);
              return true;
            }
            return true;
          },
          has(target, prop) {
            if (typeof prop === "string" && selfFields.has(prop)) return true;
            return prop in target;
          }
        }
      );
    }
  } else {
    const selfFields = /* @__PURE__ */ new Map();
    self = new Proxy(
      {
        getValue(field) {
          return selfFields.get(field);
        },
        setValue(field, value) {
          selfFields.set(field, value);
        },
        toJson() {
          return Object.fromEntries(selfFields);
        }
      },
      {
        get(target, prop) {
          if (typeof prop === "string" && !(prop in target)) {
            return selfFields.get(prop);
          }
          return target[prop];
        },
        set(_target, prop, value) {
          if (typeof prop === "string") {
            selfFields.set(prop, value);
            return true;
          }
          return true;
        },
        has(target, prop) {
          if (typeof prop === "string" && selfFields.has(prop)) return true;
          return prop in target;
        }
      }
    );
    ThisDataColl = {
      getName() {
        return "";
      },
      async loadAll() {
      },
      getCurrentItem() {
        return null;
      }
    };
  }
  const domSync = createDomSync({
    getSelf() {
      return self;
    }
  });
  const ui = createUi({ macros, routeMap, domSync });
  return {
    self,
    This: self,
    ThisDataColl,
    ui,
    appData,
    $http: http,
    crypto: cryptoApi,
    console,
    Bundle,
    macros,
    session,
    manifestPromise,
    domSync
  };
}
export {
  buildRouteMap,
  createAppContext,
  createAppData,
  createBundleCtor,
  createCryptoApi,
  createDataLayer,
  createDomSync,
  createHttp,
  createMacroStore,
  createSession,
  createUi
};
//# sourceMappingURL=index.js.map